/** @module csi/wizard/StepItem */
define([
	"dojo/_base/declare",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"underscore",
	"csi/html",
	"csi/locale",
	"csi/widget/MaterialIconWithLabel",
	"csi/widget/SimpleTextWithTitle",
	"csi/widget/Tooltip",
	"./StepData",
	"dojo/text!./StepItem.html"
], function (declare, _TemplatedMixin, _WidgetBase, _, html, locale, MaterialIconWithLabel, SimpleTextWithTitle, Tooltip, StepData, template) {

	const WIZARD_HOVER_CLASS = "stepHover";
	const WIZARD_INACTIVE_CLASS = "stepInactive";
	const WIZARD_MORE_RESULT_CLASS = "moreResultHover";
	const WIZARD_SELECTED_CLASS = "stepSelected";

	const _Widget = declare([_WidgetBase, _TemplatedMixin], {
		templateString: template,

		// extension points
		stepCardNode: null,
		titleNode: null,
		subTitleNode: null,
		enteredDataNode: null,
		moreResultNode: null,

		/** @property {number} the index of this step item widget */
		index: null,

		/** @property {string} subTitle - the subtitle of this step item widget */
		subTitle: "",

		/** @property {string} title - the title of this step item widget */
		title: "",

		/**
		 * Mark the step as Future
		 * */
		markStepFuture: function () {
			this.setInactive(true);
			this.setSelected(false);
			this.switchToIntroTitle(true);
		},

		/**
		 * Mark the step as Past
		 * */
		markStepPast: function () {
			this.setInactive(false);
			this.setSelected(false);
			this.switchToIntroTitle(false);
		},

		/**
		 * Mark the step as Present
		 * */
		markStepPresent: function () {
			this.setSelected(true);
			this.switchToIntroTitle(true);
		},

		/**
		 * @override
		 * Select the first step card
		 * */
		postCreate: function () {
			this.inherited(arguments);

			// select the first one by default
			if (this.index === 0) {
				this.setSelected(true);
			}

			// display the introduction by default
			this.switchToIntroTitle(true);
		},

		/**
		 * Populate the entered data node and show them
		 * @param {StepData} - stepData
		 * */
		setEnteredItem: function (stepData) {
			const {
				enteredDataNode,
				moreResultNode,
			} = this;

			html.destroyChildren(enteredDataNode);
			html.destroyChildren(moreResultNode);

			switch (stepData.type) {
				case StepData.WIZARD_IMAGE:
					new MaterialIconWithLabel({
						icon: "image",
						iconSize: 20,
						label: stepData.getName(),
					}).placeAt(enteredDataNode);
					break;
				case StepData.WIZARD_GALLERY:
				case StepData.WIZARD_FORM:
				case StepData.WIZARD_TABLE:
					const selections = stepData.getEnteredData().names;

					if (selections.length <= 4) {
						selections.forEach(text => {
							new SimpleTextWithTitle({
								baseClass: "csi-ellipsis-nowrap csi-wizard-entered-item",
								text
							}).placeAt(enteredDataNode);
						});
					} else {
						const list1 = selections.slice(0, 4);
						const list2 = selections.slice(4);

						// rendered as the normal item
						list1.forEach(text => {
							new SimpleTextWithTitle({
								baseClass: "csi-ellipsis-nowrap csi-wizard-entered-item",
								text
							}).placeAt(enteredDataNode);
						});

						// create a temp container for the more items
						const moreResultContainer = html.create("span", { style: { display: "none" }, }, enteredDataNode);

						list2.forEach((text) => new SimpleTextWithTitle({
							baseClass: "csi-ellipsis-nowrap",
							text
						}).placeAt(moreResultContainer));

						html.create("span", {
							"class": "csi-wizard-entered-item",
							innerText: locale.resource("MoreListResults", ["+" + list2.length]),
							onclick: (e) => {
								//avoid propagation to the parent
								e.stopPropagation();
							},
						}, this.moreResultNode);

						// create the tooltip
						new Tooltip({
							connectId: [this.moreResultNode],
							position: ["below"],
							label: moreResultContainer.innerHTML,
						});
					}

				default:
					break;
			}

			this.switchToIntroTitle(false);
		},

		/**
		 * Mark this step as inactive/active
		 * @param {boolean} on - set to true as inactive
		 * */
		setInactive: function (on) {
			this.domNode.classList.toggle(WIZARD_INACTIVE_CLASS, on);
		},

		/**
		 * Event handler for onclick
		 * 
		 * */
		selectStep: function () {
			const widget = this.getParent().getParent();
			widget.onSelectStep(this.index);
		},

		/**
		 * Mark this step as selected/ not selected
		 * @param {boolen} on - set to true as selected
		 * */
		setSelected: function (on) {
			this.domNode.classList.toggle(WIZARD_SELECTED_CLASS, on);
		},

		/**
		 * Switch between introduction mode and enter data mode
		 * @param {boolean} on - set to true as introduction mode, otherwise, enter data mode
		 * */
		switchToIntroTitle: function (on) {
			const {
				enteredDataNode,
				moreResultNode,
				subTitleNode
			} = this;

			html.setVisible(enteredDataNode, !on);
			html.setVisible(subTitleNode, on);

			if (on) {
				html.destroyChildren(moreResultNode);
			}
		},

		/**
		 * Set index
		 * @param {number} value
		 */
		_setIndexAttr: function (value) {
			this._set("index", parseInt(value));
		},

		/**
		 * Render title
		 * @param {string} value
		 */
		_setTitleAttr: function (value) {
			this._set("title", value);
			html.text(this.titleNode, value);
		},

		/**
		 * Render subtitle
		 * @param {string} value
		 */
		_setSubTitleAttr: function (value) {
			this._set("subTitle", value);
			html.clear(this.subTitleNode);
			html.text(this.subTitleNode, value);
		},

		/**
		 * Add the hover tag
		 */
		_onHover: function () {
			this.domNode.classList.add(WIZARD_HOVER_CLASS);
		},

		/**
		 * Add the hover tag
		 */
		_onHoverMoreResult: function () {
			this.moreResultNode.classList.add(WIZARD_MORE_RESULT_CLASS);
		},

		/**
		 * Remove the hover tag
		 */
		_postHover: function () {
			this.domNode.classList.remove(WIZARD_HOVER_CLASS);
		},

		/**
		 * Remove the hover tag
		 */
		_postHoverMoreResult: function () {
			this.moreResultNode.classList.remove(WIZARD_MORE_RESULT_CLASS);
		}
	});

	// specify properties of widget that can be used for csi/widget/Container
	_Widget.widgetProperties = ["index", "title", "subTitle"];

	return _Widget;
});
